// src/utils/relationalCorrelations.ts
// Relational layer correlation utilities (Sanctuary tier)

import { supabase } from '@/integrations/supabase/client';
import { subDays, format } from 'date-fns';

export interface RelationalCorrelationResult {
  correlation_type: string;
  metric_a: string;
  metric_b: string;
  avg_metric_a_low: number;
  avg_metric_a_high: number;
  avg_metric_b_low: number;
  avg_metric_b_high: number;
  interpretation: string;
  sample_size: number;
}

export interface RelationalCorrelationSummary {
  loneliness_vs_mood: RelationalCorrelationResult | null;
  connection_vs_mood: RelationalCorrelationResult | null;
  relational_wellbeing_trend: {
    direction: 'improving' | 'declining' | 'stable';
    average: number;
    change: number;
  } | null;
  data_sufficient: boolean;
  total_entries: number;
}

// Helper to calculate median
function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

// Helper to calculate average
function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

// Map daily_feeling to numeric score
const feelingToScore: Record<string, number> = {
  'amazing': 5,
  'good': 4,
  'okay': 3,
  'struggling': 2,
  'crisis': 1
};

// Map relational_feeling to numeric score
const relationalFeelingToScore: Record<string, number> = {
  'deeply_connected': 5,
  'connected': 4,
  'somewhat_connected': 3,
  'disconnected': 2,
  'very_disconnected': 1,
  'isolated': 1
};

/**
 * Correlate Loneliness (felt_lonely) with Mood
 */
export async function getLonelinessVsMoodCorrelation(
  userId: string,
  days: number = 30
): Promise<RelationalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

  // Fetch social connection logs
  const { data: socialLogs, error: socialError } = await supabase
    .from('social_connection_logs')
    .select('felt_lonely, felt_connected, date')
    .eq('user_id', userId)
    .gte('date', startDate);

  if (socialError || !socialLogs?.length) return null;

  // Fetch daily checkins for mood
  const { data: checkins, error: checkinError } = await supabase
    .from('daily_checkins')
    .select('date, daily_feeling')
    .eq('user_id', userId)
    .gte('date', startDate);

  if (checkinError || !checkins?.length) return null;

  // Map mood by date
  const moodByDate = new Map<string, number>();
  for (const c of checkins) {
    if (c.daily_feeling && feelingToScore[c.daily_feeling]) {
      moodByDate.set(c.date, feelingToScore[c.daily_feeling]);
    }
  }

  const matchedDays: { lonely: boolean; mood: number }[] = [];
  
  for (const log of socialLogs) {
    const mood = moodByDate.get(log.date);
    if (mood !== undefined && log.felt_lonely !== null) {
      matchedDays.push({
        lonely: log.felt_lonely === true,
        mood
      });
    }
  }

  if (matchedDays.length < 5) return null;

  const lonelyDays = matchedDays.filter(d => d.lonely);
  const connectedDays = matchedDays.filter(d => !d.lonely);

  if (lonelyDays.length < 2 || connectedDays.length < 2) return null;

  const avgMoodLonely = average(lonelyDays.map(d => d.mood));
  const avgMoodConnected = average(connectedDays.map(d => d.mood));
  const diff = avgMoodConnected - avgMoodLonely;

  let interpretation = '';
  if (diff > 0.8) {
    interpretation = `Days when you feel lonely tend to coincide with lower mood (${avgMoodLonely.toFixed(1)} vs ${avgMoodConnected.toFixed(1)} on connected days). Loneliness and mood often move together - neither causes the other, but they're part of the same picture.`;
  } else if (diff > 0.3) {
    interpretation = `There's a gentle pattern: when loneliness is present, your mood tends to dip slightly. This is normal - we're wired for connection.`;
  } else {
    interpretation = `Your mood appears relatively stable whether or not you feel lonely. This resilience is worth noticing.`;
  }

  return {
    correlation_type: 'loneliness_mood',
    metric_a: 'Loneliness',
    metric_b: 'Mood',
    avg_metric_a_low: 0,
    avg_metric_a_high: 1,
    avg_metric_b_low: avgMoodLonely,
    avg_metric_b_high: avgMoodConnected,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate Relational Wellbeing Score with Mood
 */
export async function getRelationalWellbeingVsMoodCorrelation(
  userId: string,
  days: number = 30
): Promise<RelationalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

  // Fetch relationship pulse checkins
  const { data: pulseLogs, error: pulseError } = await supabase
    .from('relationship_pulse_checkins')
    .select('relational_wellbeing_score, checkin_date')
    .eq('user_id', userId)
    .gte('checkin_date', startDate);

  if (pulseError || !pulseLogs?.length) return null;

  // Fetch daily checkins for mood
  const { data: checkins, error: checkinError } = await supabase
    .from('daily_checkins')
    .select('date, daily_feeling')
    .eq('user_id', userId)
    .gte('date', startDate);

  if (checkinError || !checkins?.length) return null;

  // Map mood by date
  const moodByDate = new Map<string, number>();
  for (const c of checkins) {
    if (c.daily_feeling && feelingToScore[c.daily_feeling]) {
      moodByDate.set(c.date, feelingToScore[c.daily_feeling]);
    }
  }

  const matchedDays: { relational: number; mood: number }[] = [];
  
  for (const log of pulseLogs) {
    const mood = moodByDate.get(log.checkin_date);
    if (mood !== undefined && log.relational_wellbeing_score !== null) {
      matchedDays.push({
        relational: log.relational_wellbeing_score,
        mood
      });
    }
  }

  if (matchedDays.length < 5) return null;

  // Split by median relational score
  const relationalMedian = median(matchedDays.map(d => d.relational));
  const lowRelational = matchedDays.filter(d => d.relational < relationalMedian);
  const highRelational = matchedDays.filter(d => d.relational >= relationalMedian);

  if (lowRelational.length < 2 || highRelational.length < 2) return null;

  const avgMoodLow = average(lowRelational.map(d => d.mood));
  const avgMoodHigh = average(highRelational.map(d => d.mood));
  const diff = avgMoodHigh - avgMoodLow;

  let interpretation = '';
  if (diff > 0.5) {
    interpretation = `Higher relational wellbeing scores correlate with better mood (${avgMoodHigh.toFixed(1)} vs ${avgMoodLow.toFixed(1)}). Feeling connected to others seems to support your emotional state.`;
  } else if (diff > 0.2) {
    interpretation = `There's a gentle link between how connected you feel and your overall mood. Relationships seem to matter for your wellbeing.`;
  } else {
    interpretation = `Your mood remains relatively stable across different levels of relational wellbeing. This stability may reflect inner resources.`;
  }

  return {
    correlation_type: 'relational_wellbeing_mood',
    metric_a: 'Relational Wellbeing',
    metric_b: 'Mood',
    avg_metric_a_low: average(lowRelational.map(d => d.relational)),
    avg_metric_a_high: average(highRelational.map(d => d.relational)),
    avg_metric_b_low: avgMoodLow,
    avg_metric_b_high: avgMoodHigh,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate Friend Support with Anxiety/Overwhelm
 */
export async function getFriendSupportVsOverwhelmCorrelation(
  userId: string,
  days: number = 30
): Promise<RelationalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

  // Fetch relationship pulse checkins
  const { data: pulseLogs, error: pulseError } = await supabase
    .from('relationship_pulse_checkins')
    .select('friend_support, checkin_date')
    .eq('user_id', userId)
    .gte('checkin_date', startDate);

  if (pulseError || !pulseLogs?.length) return null;

  // Fetch emotional symptom logs for anxiety
  const { data: emotionalLogs, error: emotionalError } = await supabase
    .from('emotional_symptom_logs')
    .select('date, anxiety_level')
    .eq('user_id', userId)
    .gte('date', startDate);

  if (emotionalError) return null;

  // Map anxiety by date
  const anxietyByDate = new Map<string, number>();
  emotionalLogs?.forEach(e => {
    if (e.anxiety_level !== null) {
      anxietyByDate.set(e.date, e.anxiety_level);
    }
  });

  const matchedDays: { support: number; anxiety: number }[] = [];
  
  for (const log of pulseLogs) {
    const anxiety = anxietyByDate.get(log.checkin_date);
    if (anxiety !== undefined && log.friend_support !== null) {
      matchedDays.push({
        support: log.friend_support,
        anxiety
      });
    }
  }

  if (matchedDays.length < 5) return null;

  // Split by median friend support
  const supportMedian = median(matchedDays.map(d => d.support));
  const lowSupport = matchedDays.filter(d => d.support < supportMedian);
  const highSupport = matchedDays.filter(d => d.support >= supportMedian);

  if (lowSupport.length < 2 || highSupport.length < 2) return null;

  const avgAnxietyLow = average(lowSupport.map(d => d.anxiety));
  const avgAnxietyHigh = average(highSupport.map(d => d.anxiety));
  const diff = avgAnxietyLow - avgAnxietyHigh;

  let interpretation = '';
  if (diff > 1) {
    interpretation = `When you feel more supported by friends, your anxiety tends to be lower (${avgAnxietyHigh.toFixed(1)} vs ${avgAnxietyLow.toFixed(1)}). Social support appears to buffer stress for you.`;
  } else if (diff > 0.3) {
    interpretation = `There's a gentle pattern: feeling supported by friends coincides with slightly lower anxiety. Connection may help regulate your nervous system.`;
  } else {
    interpretation = `Anxiety levels appear similar regardless of friend support levels. Other factors may be more influential for you.`;
  }

  return {
    correlation_type: 'friend_support_anxiety',
    metric_a: 'Friend Support',
    metric_b: 'Anxiety',
    avg_metric_a_low: average(lowSupport.map(d => d.support)),
    avg_metric_a_high: average(highSupport.map(d => d.support)),
    avg_metric_b_low: avgAnxietyLow,
    avg_metric_b_high: avgAnxietyHigh,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate Partner Connection with Emotional Stability
 */
export async function getPartnerConnectionVsEmotionalStabilityCorrelation(
  userId: string,
  days: number = 30
): Promise<RelationalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

  // Fetch relationship pulse checkins
  const { data: pulseLogs, error: pulseError } = await supabase
    .from('relationship_pulse_checkins')
    .select('partner_connection, checkin_date')
    .eq('user_id', userId)
    .gte('checkin_date', startDate);

  if (pulseError || !pulseLogs?.length) return null;

  // Fetch daily checkins for mood variability
  const { data: checkins, error: checkinError } = await supabase
    .from('daily_checkins')
    .select('date, daily_feeling')
    .eq('user_id', userId)
    .gte('date', startDate);

  if (checkinError || !checkins?.length) return null;

  // Map mood by date
  const moodByDate = new Map<string, number>();
  for (const c of checkins) {
    if (c.daily_feeling && feelingToScore[c.daily_feeling]) {
      moodByDate.set(c.date, feelingToScore[c.daily_feeling]);
    }
  }

  const matchedDays: { partner: number; mood: number }[] = [];
  
  for (const log of pulseLogs) {
    const mood = moodByDate.get(log.checkin_date);
    if (mood !== undefined && log.partner_connection !== null) {
      matchedDays.push({
        partner: log.partner_connection,
        mood
      });
    }
  }

  if (matchedDays.length < 5) return null;

  // Split by median partner connection
  const partnerMedian = median(matchedDays.map(d => d.partner));
  const lowPartner = matchedDays.filter(d => d.partner < partnerMedian);
  const highPartner = matchedDays.filter(d => d.partner >= partnerMedian);

  if (lowPartner.length < 2 || highPartner.length < 2) return null;

  const avgMoodLow = average(lowPartner.map(d => d.mood));
  const avgMoodHigh = average(highPartner.map(d => d.mood));
  const diff = avgMoodHigh - avgMoodLow;

  let interpretation = '';
  if (diff > 0.5) {
    interpretation = `Stronger partner connection correlates with better mood (${avgMoodHigh.toFixed(1)} vs ${avgMoodLow.toFixed(1)}). Your primary relationship seems to affect how you feel overall.`;
  } else if (diff > 0.2) {
    interpretation = `There's a gentle link between partner connection and mood. This relationship appears to matter for your emotional state.`;
  } else {
    interpretation = `Your mood remains relatively consistent across different levels of partner connection. Other factors may be more central for you.`;
  }

  return {
    correlation_type: 'partner_connection_mood',
    metric_a: 'Partner Connection',
    metric_b: 'Mood',
    avg_metric_a_low: average(lowPartner.map(d => d.partner)),
    avg_metric_a_high: average(highPartner.map(d => d.partner)),
    avg_metric_b_low: avgMoodLow,
    avg_metric_b_high: avgMoodHigh,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate Self-Compassion with Mood
 */
export async function getSelfCompassionVsMoodCorrelation(
  userId: string,
  days: number = 30
): Promise<RelationalCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

  // Fetch relationship pulse checkins
  const { data: pulseLogs, error: pulseError } = await supabase
    .from('relationship_pulse_checkins')
    .select('self_compassion, checkin_date')
    .eq('user_id', userId)
    .gte('checkin_date', startDate);

  if (pulseError || !pulseLogs?.length) return null;

  // Fetch daily checkins for mood
  const { data: checkins, error: checkinError } = await supabase
    .from('daily_checkins')
    .select('date, daily_feeling')
    .eq('user_id', userId)
    .gte('date', startDate);

  if (checkinError || !checkins?.length) return null;

  // Map mood by date
  const moodByDate = new Map<string, number>();
  for (const c of checkins) {
    if (c.daily_feeling && feelingToScore[c.daily_feeling]) {
      moodByDate.set(c.date, feelingToScore[c.daily_feeling]);
    }
  }

  const matchedDays: { selfCompassion: number; mood: number }[] = [];
  
  for (const log of pulseLogs) {
    const mood = moodByDate.get(log.checkin_date);
    if (mood !== undefined && log.self_compassion !== null) {
      matchedDays.push({
        selfCompassion: log.self_compassion,
        mood
      });
    }
  }

  if (matchedDays.length < 5) return null;

  // Split by median self-compassion
  const compassionMedian = median(matchedDays.map(d => d.selfCompassion));
  const lowCompassion = matchedDays.filter(d => d.selfCompassion < compassionMedian);
  const highCompassion = matchedDays.filter(d => d.selfCompassion >= compassionMedian);

  if (lowCompassion.length < 2 || highCompassion.length < 2) return null;

  const avgMoodLow = average(lowCompassion.map(d => d.mood));
  const avgMoodHigh = average(highCompassion.map(d => d.mood));
  const diff = avgMoodHigh - avgMoodLow;

  let interpretation = '';
  if (diff > 0.5) {
    interpretation = `Higher self-compassion strongly correlates with better mood (${avgMoodHigh.toFixed(1)} vs ${avgMoodLow.toFixed(1)}). Being kind to yourself appears to support your emotional wellbeing.`;
  } else if (diff > 0.2) {
    interpretation = `There's a gentle link between self-compassion and mood. The way you relate to yourself seems to matter.`;
  } else {
    interpretation = `Your mood appears stable across different levels of self-compassion. This may reflect other supportive factors in your life.`;
  }

  return {
    correlation_type: 'self_compassion_mood',
    metric_a: 'Self-Compassion',
    metric_b: 'Mood',
    avg_metric_a_low: average(lowCompassion.map(d => d.selfCompassion)),
    avg_metric_a_high: average(highCompassion.map(d => d.selfCompassion)),
    avg_metric_b_low: avgMoodLow,
    avg_metric_b_high: avgMoodHigh,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Calculate relational wellbeing trend
 */
export async function getRelationalWellbeingTrend(
  userId: string,
  days: number = 30
): Promise<{ direction: 'improving' | 'declining' | 'stable'; average: number; change: number } | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

  const { data: pulseLogs, error } = await supabase
    .from('relationship_pulse_checkins')
    .select('relational_wellbeing_score, checkin_date')
    .eq('user_id', userId)
    .gte('checkin_date', startDate)
    .order('checkin_date', { ascending: true });

  if (error || !pulseLogs?.length || pulseLogs.length < 3) return null;

  const scores = pulseLogs
    .filter(p => p.relational_wellbeing_score !== null)
    .map(p => p.relational_wellbeing_score as number);

  if (scores.length < 3) return null;

  const avg = average(scores);
  const midpoint = Math.floor(scores.length / 2);
  const firstHalf = scores.slice(0, midpoint);
  const secondHalf = scores.slice(midpoint);
  
  const firstAvg = average(firstHalf);
  const secondAvg = average(secondHalf);
  const change = secondAvg - firstAvg;

  let direction: 'improving' | 'declining' | 'stable' = 'stable';
  if (change > 0.5) direction = 'improving';
  else if (change < -0.5) direction = 'declining';

  return { direction, average: avg, change };
}

/**
 * Get all relational correlations for a user
 */
export async function getRelationalCorrelations(
  userId: string,
  days: number = 30
): Promise<RelationalCorrelationSummary> {
  // Check data sufficiency first
  const { data: pulseCount } = await supabase
    .from('relationship_pulse_checkins')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId);

  const totalEntries = pulseCount?.length || 0;
  const dataSufficient = totalEntries >= 5;

  const [
    lonelinessVsMood,
    connectionVsMood,
    trend
  ] = await Promise.all([
    getLonelinessVsMoodCorrelation(userId, days).catch(() => null),
    getRelationalWellbeingVsMoodCorrelation(userId, days).catch(() => null),
    getRelationalWellbeingTrend(userId, days).catch(() => null)
  ]);

  return {
    loneliness_vs_mood: lonelinessVsMood,
    connection_vs_mood: connectionVsMood,
    relational_wellbeing_trend: trend,
    data_sufficient: dataSufficient,
    total_entries: totalEntries
  };
}

/**
 * Get all relational correlations as a flat array
 */
export async function getAllRelationalCorrelations(
  userId: string,
  days: number = 30
): Promise<RelationalCorrelationResult[]> {
  const results: RelationalCorrelationResult[] = [];

  const correlationFns = [
    getLonelinessVsMoodCorrelation,
    getRelationalWellbeingVsMoodCorrelation,
    getFriendSupportVsOverwhelmCorrelation,
    getPartnerConnectionVsEmotionalStabilityCorrelation,
    getSelfCompassionVsMoodCorrelation
  ];

  const correlations = await Promise.all(
    correlationFns.map(fn => fn(userId, days).catch(() => null))
  );

  for (const c of correlations) {
    if (c !== null && c.interpretation) {
      results.push(c);
    }
  }

  return results;
}
